import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../components/Icon';

const IMG = `${process.env.PUBLIC_URL}/images`;
const COIMG = `${IMG}/Contact`;

// Backend mail endpoint (Node + nodemailer in /server). Override with
// REACT_APP_CONTACT_API if the server runs on a different host/path.
const CONTACT_API = process.env.REACT_APP_CONTACT_API || '/api/contact';

const EMPTY_FORM = {
  fullName: '',
  email: '',
  phone: '',
  inquiryType: '',
  subject: '',
  message: '',
};

const heroStats = [
  { icon: 'users', value: '10000+', label: 'Community', sub: 'Growing Together' },
  { icon: 'globe', value: '30+', label: 'Cities', sub: 'Across the Globe' },
  {
    icon: 'heart',
    value: '20000+',
    label: 'Happy Experiences',
    sub: 'Spreading Joy',
  },
];

const helpCards = [
  {
    icon: 'message-square',
    color: '#a855f7',
    title: 'General Inquiry',
    desc: 'Questions about Caladium Systems and our ecosystem.',
  },
  {
    icon: 'handshake',
    color: '#38bdf8',
    title: 'Partnerships',
    desc: 'Explore partnerships and collaboration opportunities.',
  },
  {
    icon: 'briefcase',
    color: '#f59e0b',
    title: 'Careers',
    desc: 'Join our team and build the future of celebrations.',
  },
  {
    icon: 'megaphone',
    color: '#ec4899',
    title: 'Media & Press',
    desc: 'For media inquiries, interviews and press related matters.',
  },
  {
    icon: 'trending-up',
    color: '#22c55e',
    title: 'Investors',
    desc: 'For investor relations and business development.',
  },
  {
    icon: 'star',
    color: '#a855f7',
    title: 'Brand Collaborations',
    desc: 'Work with our brands and create magical experiences.',
  },
];

const inquiryTypes = [
  'General Inquiry',
  'Partnerships',
  'Careers',
  'Media & Press',
  'Investors',
  'Brand Collaborations',
];

const offices = [
  {
    flag: 'India – Headquarters',
    image: `${IMG}/mc_building.png`,
    address: [
      'Caladium Systems Pvt Limited',
      'No: 18/8, 29th Cross St, Indira Nagar,',
      'Adyar, Chennai – 600020, Tamil Nadu, India.',
    ],
    email: 'info@caladium.in',
    mapEmbed:
      'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3887.7099484477803!2d80.25171707484175!3d12.990394387326688!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a5267ecb42a69f3%3A0xa9328da5790f1773!2sCaladium%20Systems%20(Main%20office)!5e0!3m2!1sen!2sin!4v1781262974390!5m2!1sen!2sin',
  },
];

export default function Contact() {
  const [agreed, setAgreed] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState({ state: 'idle', message: '' });

  const update = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  // Phone: keep digits only, capped at 10.
  const updatePhone = (e) =>
    setForm((f) => ({ ...f, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }));

  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
  const isValidPhone = (v) => /^\d{10}$/.test(v.trim());

  const onSubmit = async (e) => {
    e.preventDefault();
    if (status.state === 'sending') return;

    if (!isValidEmail(form.email)) {
      setStatus({
        state: 'error',
        message: 'Please enter a valid email address.',
      });
      return;
    }

    if (!isValidPhone(form.phone)) {
      setStatus({
        state: 'error',
        message: 'Please enter a valid 10-digit mobile number.',
      });
      return;
    }

    if (!agreed) {
      setStatus({
        state: 'error',
        message: 'Please agree to the Privacy Policy and Terms of Service.',
      });
      return;
    }

    setStatus({ state: 'sending', message: '' });

    try {
      const res = await fetch(CONTACT_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'Send failed');
      }

      setStatus({
        state: 'success',
        message: 'Thank you! Your message has been sent. We’ll get back to you shortly.',
      });
      setForm(EMPTY_FORM);
      setAgreed(false);
    } catch (err) {
      setStatus({
        state: 'error',
        message: 'Something went wrong. Please try again or email info@caladium.in.',
      });
    }
  };

  const sending = status.state === 'sending';

  return (
    <div className="contact-page">
      {/* =================== HERO =================== */}
      <section className="ct-hero">
        <div
          className="ct-hero__bg"
          style={{ backgroundImage: `url(${COIMG}/Contact-1.jpeg)` }}
        />
        <div className="container container--wide ct-hero__inner">
          <div className="crumb">
            <Link to="/">Home</Link>
            <span>/</span>
            <span>Contact Us</span>
          </div>

          <div className="ct-hero__copy">
            <h1 className="ct-hero__title">
              Let&rsquo;s Build
              <br />
              the <span className="grad-pink">Future</span> Together
            </h1>
            <span className="ct-hero__rule" />
            <p className="ct-hero__lead">
              Have a question, an idea, or a partnership opportunity? We&rsquo;d
              love to hear from you. Reach out to us and our team will get back
              to you shortly.
            </p>

            <div className="ct-hero__stats">
              {heroStats.map((s) => (
                <div className="ct-stat" key={s.label}>
                  <span className="ct-stat__icon">
                    <Icon name={s.icon} size={22} />
                  </span>
                  <div className="ct-stat__text">
                    <span className="ct-stat__sub">{s.sub}</span>
                    <strong className="ct-stat__value">{s.value}</strong>
                    <span className="ct-stat__label">{s.label}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* =================== HELP + FORM =================== */}
      <section className="ct-main-sec">
        <div className="container container--wide ct-main">
          {/* LEFT: How Can We Help You */}
          <div className="ct-help">
            <h2 className="ct-section__title">How Can We Help You?</h2>
            <div className="ct-help__grid">
              {helpCards.map((c) => (
                <div
                  className="ct-help__card"
                  key={c.title}
                  style={{ '--c': c.color }}
                >
                  <span className="ct-help__icon">
                    <Icon name={c.icon} size={26} />
                  </span>
                  <h3>{c.title}</h3>
                  <p>{c.desc}</p>
                  <span className="ct-help__arrow">
                    <Icon name="arrow-right" size={16} />
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT: Send Us a Message */}
          <div className="ct-form-card">
            <div className="ct-form__head">
              <h2 className="ct-section__title">Send Us a Message</h2>
              <span className="ct-form__meta">
                <Icon name="clock" size={14} /> We typically respond within 24 hours
              </span>
            </div>

            <form className="ct-form" onSubmit={onSubmit}>
              <div className="ct-form__row">
                <input
                  className="ct-input"
                  type="text"
                  placeholder="Full Name*"
                  value={form.fullName}
                  onChange={update('fullName')}
                  required
                />
                <input
                  className="ct-input"
                  type="email"
                  placeholder="Email Address*"
                  value={form.email}
                  onChange={update('email')}
                  required
                />
              </div>
              <div className="ct-form__row">
                <input
                  className="ct-input"
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="Phone Number* (10 digits)"
                  value={form.phone}
                  onChange={updatePhone}
                  required
                />
                <div className="ct-select">
                  <select
                    className="ct-input"
                    value={form.inquiryType}
                    onChange={update('inquiryType')}
                    required
                  >
                    <option value="" disabled>Inquiry Type*</option>
                    {inquiryTypes.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                  <Icon name="chevron-down" size={16} className="ct-select__caret" />
                </div>
              </div>
              <input
                className="ct-input"
                type="text"
                placeholder="Subject*"
                value={form.subject}
                onChange={update('subject')}
                required
              />
              <textarea
                className="ct-input ct-textarea"
                placeholder="Message*"
                rows="5"
                value={form.message}
                onChange={update('message')}
                required
              />

              <label className="ct-check">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                />
                <span>
                  I agree to the{' '}
                  <a href="#privacy">Privacy Policy</a> and{' '}
                  <a href="#terms">Terms of Service</a>.
                </span>
              </label>

              {status.state === 'success' && (
                <p className="ct-form__status ct-form__status--ok">
                  {status.message}
                </p>
              )}
              {status.state === 'error' && (
                <p className="ct-form__status ct-form__status--err">
                  {status.message}
                </p>
              )}

              <button type="submit" className="ct-submit" disabled={sending}>
                {sending ? 'Sending…' : 'Send Message'}{' '}
                <Icon name="send" size={18} />
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* =================== OUR OFFICES =================== */}
      <section className="ct-offices-sec">
        <div className="container container--wide">
          <div className="ct-offices">
            <header className="ct-offices__head">
              <h2 className="ct-section__title">Our Offices</h2>
              <p>Connecting globally to create unforgettable experiences.</p>
            </header>

            <div className="ct-office-grid">
              {offices.map((o) => (
                <article className="ct-office-row" key={o.flag}>
                  {/* Card 1: photo + details */}
                  <div className="ct-office-card">
                    <div className="ct-office__photo">
                      <img src={o.image} alt={o.flag} />
                    </div>
                    <div className="ct-office__info">
                      <h3 className="ct-office__name">
                        <Icon name="map-pin" size={16} /> {o.flag}
                      </h3>
                      <div className="ct-office__addr">
                        {o.address.map((line) => (
                          <span key={line}>{line}</span>
                        ))}
                      </div>
                      {o.phone && (
                        <div className="ct-office__line">
                          <Icon name="phone" size={15} /> {o.phone}
                        </div>
                      )}
                      <a
                        className="ct-office__line ct-office__link"
                        href={`mailto:${o.email}`}
                      >
                        <Icon name="mail" size={15} /> {o.email}
                      </a>
                    </div>
                  </div>

                  {/* Card 2: map */}
                  <div className="ct-office-card ct-office-card--map">
                    {o.mapEmbed ? (
                      <iframe
                        className="ct-office__map ct-office__map--live"
                        src={o.mapEmbed}
                        title={`${o.flag} location`}
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        allowFullScreen
                      />
                    ) : (
                      <div className="ct-office__map" aria-hidden="true">
                        <Icon name="map-pin" size={28} />
                      </div>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
